import React, { useEffect, useRef } from 'react';
import './DrumCounterFace.css';

// All ten digits stacked in a vertical strip inside each drum's window. The
// drum shows one digit by translating the strip so that row lives in the
// window; changing which row is shown is what produces the roll — CSS
// transitions the transform, so intermediate digits visibly pass through the
// window on the way, the way a real mechanical drum counter rolls.
const DIGIT_STRIP = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * Truncates (never rounds) a dollar float to 3 decimal places and splits it
 * into the 4 integer + 3 fractional digit characters the drum face renders.
 * Uses toFixed(6) + string slicing rather than Math.floor(n * 1000) / 1000 —
 * the latter is vulnerable to float-multiplication drift (e.g. 13.47 * 1000
 * can land a hair under 13470), which would silently truncate one digit too
 * far. toFixed rounds the double to a correct decimal string first; slicing
 * (not rounding) the first 3 fractional characters is then a true truncation.
 *
 * The integer part is clamped to the 4 drums this face has (odometer
 * rollover convention — keep the low-order digits) since $9,999.999 is far
 * beyond any real OpenRouter balance this product expects to show.
 */
export function truncateBalanceDigits(balance) {
  const abs = Math.max(0, balance);
  const [intStr, fracStrRaw = ''] = abs.toFixed(6).split('.');
  const fracStr = (fracStrRaw + '000').slice(0, 3);
  const intDigits = intStr.padStart(4, '0').slice(-4).split('');
  const fracDigits = fracStr.split('');
  return { intDigits, fracDigits };
}

function Drum({ digit, sub, dash }) {
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
  }, []);

  const className = `fuel-drum${sub ? ' fuel-drum--sub' : ''}${dash ? ' fuel-drum--dash' : ''}`;

  if (dash) {
    return (
      <span className={className} data-testid="fuel-drum" data-value="-">
        <span className="fuel-drum__window">
          <span className="fuel-drum__digit">&ndash;</span>
        </span>
      </span>
    );
  }

  const n = Number(digit);
  return (
    <span className={className} data-testid="fuel-drum" data-value={String(n)}>
      <span className="fuel-drum__window">
        <span
          className="fuel-drum__reel"
          style={{
            transform: `translateY(-${n * 10}%)`,
            // Skip the transition on first paint — there's no prior digit to
            // roll FROM, so the correct value should just be there, not
            // animate up from '0'. Every value change after mount rolls.
            transition: mountedRef.current ? undefined : 'none',
          }}
        >
          {DIGIT_STRIP.map((d) => (
            <span key={d} className="fuel-drum__digit">{d}</span>
          ))}
        </span>
      </span>
    </span>
  );
}

/**
 * The Fuel Gauge's instrument face: a 7-drum mechanical counter reading the
 * exact USD balance as $0,000.000, plus a low-fuel lamp. Renders the dash
 * treatment instead of drums when there's no truthful finite balance to show
 * (no key configured, fetch error, or a pay-as-you-go/unlimited account —
 * none of these have a bounded dollar figure to roll into place), per the
 * "never fake a zero balance" requirement — dashes are always honest, a
 * fabricated number never is.
 */
export function DrumCounterFace({ balance, available, lampLit }) {
  const { intDigits, fracDigits } = available
    ? truncateBalanceDigits(balance)
    : { intDigits: ['-', '-', '-', '-'], fracDigits: ['-', '-', '-'] };

  return (
    <span className="fuel-gauge__face" aria-hidden="true">
      <span className="fuel-gauge__dollar">$</span>
      <span className="fuel-gauge__drums">
        <Drum digit={intDigits[0]} dash={!available} />
        <span className="fuel-gauge__sep">,</span>
        <Drum digit={intDigits[1]} dash={!available} />
        <Drum digit={intDigits[2]} dash={!available} />
        <Drum digit={intDigits[3]} dash={!available} />
        <span className="fuel-gauge__sep">.</span>
        <Drum digit={fracDigits[0]} dash={!available} />
        <Drum digit={fracDigits[1]} dash={!available} />
        <Drum digit={fracDigits[2]} dash={!available} sub />
      </span>
      <span
        className={`fuel-gauge__lamp${lampLit ? ' fuel-gauge__lamp--lit' : ''}`}
        title={lampLit ? 'Low balance' : undefined}
      />
    </span>
  );
}
